import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn } from '@/Components/motion';
import { Link } from 'react-router-dom';
import { Separator } from '@/Components/ui/separator';
import { AlertTriangle } from 'lucide-react';
import { siteConfig } from '@/config/site';

const sections = [
    {
        title: 'General Risk Warning',
        content:
            'Trading in financial markets, including forex, commodities, indices, and cryptocurrencies, involves substantial risk of loss and is not suitable for all investors. The high degree of leverage available in these markets can work against you as well as for you. Before deciding to trade, you should carefully consider your investment objectives, level of experience, and risk appetite. There is a possibility that you could sustain a loss of some or all of your initial investment, and therefore you should not invest money that you cannot afford to lose.',
    },
    {
        title: 'Simulated Trading',
        content:
            `${siteConfig.brand.name} provides simulated trading environments for evaluation and funded account purposes. Simulated trading results do not represent actual trading and may not accurately reflect the performance that would have been achieved in a live trading environment. Simulated accounts may differ from live accounts in areas such as execution speed, slippage, liquidity, and market impact. Past performance in a simulated environment is not indicative of future results in live market conditions.`,
    },
    {
        title: 'Performance Disclaimers',
        content:
            `Any performance data, statistics, or results presented on the ${siteConfig.brand.name} platform or marketing materials are for illustrative purposes only and do not guarantee future performance. Trading results can vary significantly based on market conditions, individual skill, and other factors. Testimonials and success stories represent individual experiences and are not representative of all users. No representation is being made that any account will or is likely to achieve profits or losses similar to those shown.`,
    },
    {
        title: 'No Financial Advice',
        content:
            `${siteConfig.brand.name} does not provide financial, investment, legal, or tax advice. All information, tools, and educational content provided on our platform are for informational and educational purposes only and should not be construed as personalized financial advice. You should consult with qualified financial, legal, and tax professionals before making any investment decisions. ${siteConfig.brand.name} is not responsible for any trading decisions made based on information provided on our platform or by our staff.`,
    },
    {
        title: 'Restricted Regions',
        content:
            `${siteConfig.brand.name} services are not available to residents or citizens of certain restricted jurisdictions. These include, but are not limited to, the United States of America, Cuba, Iraq, Myanmar (Burma), North Korea, and Sudan. This list may be updated at any time without prior notice. It is your sole responsibility to determine whether your jurisdiction permits you to use our services. Accessing or attempting to access ${siteConfig.brand.name} services from a restricted region is a violation of these terms and may result in immediate account termination.`,
    },
];

export default function RiskDisclosure() {
    return (
        <AppLayout title={`Risk Disclosure - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <FadeIn>
                    <div className="text-center max-w-4xl mx-auto">
                        <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                            Legal
                        </p>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            Risk{' '}
                            <span className="text-gradient">Disclosure</span>
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                            Understanding the risks involved in trading is
                            essential. Please read this disclosure carefully.
                        </p>
                    </div>
                </FadeIn>
            </Section>

            {/* Risk Warning Banner */}
            <Section className="py-8 bg-background">
                <FadeIn>
                    <div className="max-w-4xl mx-auto">
                        <div className="flex items-start gap-4 rounded-xl border border-primary/20 bg-primary/5 p-6">
                            <AlertTriangle className="h-6 w-6 text-primary shrink-0 mt-0.5" />
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                <strong className="text-foreground">
                                    Important:
                                </strong>{' '}
                                Trading leveraged products carries a high level
                                of risk and may not be suitable for all
                                investors. You should ensure you fully understand
                                the risks involved before trading.
                            </p>
                        </div>
                    </div>
                </FadeIn>
            </Section>

            {/* Content */}
            <Section className="py-16 bg-muted/30 border-t border-border">
                <FadeIn>
                    <Card className="max-w-4xl mx-auto rounded-xl border border-border bg-card/80 backdrop-blur-sm">
                        <CardContent className="p-8 md:p-12">
                            <p className="text-sm text-muted-foreground mb-10">
                                Last updated: March 2026
                            </p>

                            <div className="space-y-10">
                                {sections.map((section, index) => (
                                    <div key={section.title}>
                                        <h2 className="text-xl font-semibold text-foreground mb-3">
                                            {index + 1}. {section.title}
                                        </h2>
                                        <p className="text-muted-foreground leading-relaxed">
                                            {section.content}
                                        </p>
                                        {index < sections.length - 1 && (
                                            <Separator className="mt-10" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </FadeIn>
            </Section>

            {/* Bottom spacer */}
            <Section className="py-16 bg-background border-t border-border">
                <div className="text-center max-w-2xl mx-auto">
                    <p className="text-sm text-muted-foreground">
                        Questions about risk?{' '}
                        <Link
                            to="/contact"
                            className="text-primary hover:underline"
                        >
                            Contact us
                        </Link>
                    </p>
                </div>
            </Section>
        </AppLayout>
    );
}
